/**
 * Issue #174 — Single source of truth for the public industry-sample sites
 * surfaced from the LP /samples page.
 *
 * Consumed by:
 *   - apps/cms /samples listing (apps/cms/src/app/(front)/samples/page.tsx)
 *   - apps/admin /samples listing (apps/admin/src/routes/(public)/samples/+page.svelte)
 *   - apps/cms build:sample-sites script (apps/cms/scripts/build-sample-sites.ts)
 *
 * `tenant` is both the fixture file basename in apps/builder/themes/<theme>/fixtures/<tenant>.json
 * and the production subdomain (<tenant>.daigakus.com). `theme` selects which
 * Astro theme directory the builder reads from. `thumb` is a path served from
 * apps/cms/public.
 */
export type SampleEntry = {
  readonly tenant: string;
  readonly theme: string;
  readonly displayName: string;
  readonly category: string;
  readonly description: string;
  readonly thumb: string;
};

// Issue #174 — `Object.freeze` at module load so a runtime `as any`
// mutation in a downstream consumer can't silently corrupt the catalog
// that flows into reserved-slug seeds, build-time URL generation, and
// DNS-label asserts. TypeScript's `readonly` is compile-time only; the
// catalog is treated as a security-shaped invariant at runtime.
export const SAMPLE_CATALOG: readonly SampleEntry[] = Object.freeze(
  [
    {
      tenant: 'beauty-demo',
      theme: 'beauty',
      displayName: 'サロン アカリ',
      category: '美容室・サロン',
      description: 'メニュー・スタイリスト紹介・予約導線を備えた美容室向けサイト',
      thumb: '/lp/template-beauty.webp',
    },
    {
      tenant: 'restaurant-demo',
      theme: 'restaurant',
      displayName: '和食処 木洩れ日',
      category: '飲食・カフェ',
      description: '営業時間・メニュー・アクセスを見やすく掲載した飲食店向けサイト',
      thumb: '/lp/template-cafe.webp',
    },
    {
      tenant: 'law-demo',
      theme: 'law',
      displayName: '山田総合法律事務所',
      category: '士業・コンサル',
      description: '取扱分野・弁護士紹介・実績を整然と並べた法律事務所向けサイト',
      thumb: '/lp/template-legal.webp',
    },
    {
      tenant: 'tax-demo',
      theme: 'law',
      displayName: '佐藤税理士・会計事務所',
      category: '士業・コンサル',
      description: '税務顧問・確定申告・相続対策を揃えた会計事務所向けサイト',
      thumb: '/lp/template-tax.webp',
    },
    {
      tenant: 'administrative-demo',
      theme: 'law',
      displayName: '鈴木行政書士事務所',
      category: '士業・コンサル',
      description: '許認可・会社設立・在留資格を扱う行政書士向けサイト',
      thumb: '/lp/template-administrative.webp',
    },
    {
      tenant: 'labor-demo',
      theme: 'law',
      displayName: '田中社会保険労務士事務所',
      category: '士業・コンサル',
      description: '労務顧問・助成金・就業規則を揃えた社労士向けサイト',
      thumb: '/lp/template-labor.webp',
    },
    {
      tenant: 'judicial-demo',
      theme: 'law',
      displayName: '高橋司法書士事務所',
      category: '士業・コンサル',
      description: '不動産登記・相続手続き・成年後見を扱う司法書士向けサイト',
      thumb: '/lp/template-judicial.webp',
    },
    {
      tenant: 'consulting-demo',
      theme: 'law',
      displayName: '中村経営コンサルティング',
      category: '士業・コンサル',
      description: '経営戦略・業務改善・DX推進を扱うコンサル向けサイト',
      thumb: '/lp/template-consulting.webp',
    },
    {
      tenant: 'acme',
      theme: 'corporate',
      displayName: '株式会社 ACME 製作所',
      category: '会社・コーポレート',
      description: '事業内容・採用情報・ニュースを掲載した中小企業向けコーポレートサイト',
      thumb: '/lp/template-corporate.webp',
    },
    {
      tenant: 'retail-demo',
      theme: 'retail',
      displayName: '暮らしの雑貨 ハレノヒ',
      category: '小売・ショップ',
      description: '商品紹介・店舗情報を揃えた小売店向けサイト',
      thumb: '/lp/template-retail.webp',
    },
    {
      tenant: 'medical-demo',
      theme: 'medical',
      displayName: 'あおぞら内科クリニック',
      category: 'クリニック・医療',
      description: '診療案内・予約・アクセスを備えたクリニック向けサイト',
      thumb: '/lp/template-medical.webp',
    },
  ].map((entry) => Object.freeze(entry)) as readonly SampleEntry[],
);
